import { useEffect, useRef, type ReactNode } from "react";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: "sm" | "md" | "lg";
};

const sizeClasses: Record<NonNullable<Props["size"]>, string> = {
  sm: "max-w-lg",
  md: "max-w-2xl",
  lg: "max-w-4xl",
};

// Portal-style modal: ink/40 semi-transparent backdrop, the panel is a
// `.card` with a heading row using `.heading-underline`. Click-outside and
// Escape both close. The body section receives whatever the caller passes;
// footer button rows are the caller's responsibility (see ConfirmDialog).
export function Modal({ open, onClose, title, children, size = "md" }: Props) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      dialogRef.current?.focus();
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 px-4 py-12 transition"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        className={`card w-full ${sizeClasses[size]} p-6 outline-none transition sm:p-8`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <h2
            id="modal-title"
            className="heading-underline text-lg font-extrabold uppercase tracking-[0.02em] text-ink"
          >
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="-mt-1 rounded-full p-1.5 text-ink-soft transition hover:bg-bg hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/40"
          >
            <span aria-hidden="true">{"✕"}</span>
          </button>
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
}
